import React, { useEffect, useMemo, useRef, useState } from "react";
import { useResizeObserver } from "usehooks-ts";

import { faPlus } from "@performyard/fontawesome/pro-solid-svg-icons";
import { EmployeeWithoutGoalsModel } from "@performyard/models";
import { ButtonStandard } from "@performyard/py-components";
import { camelToSnake } from "@performyard/utils-isomorphic";

import DownloadButton from "../../../../../components.smart/DownloadButton/DownloadButton";
import Pagination from "../../../../../components/Pagination";
import { SideNavigationLink } from "../../../../../components/SideNavigation";
import Spacer from "../../../../../components/Spacer";
import ScrollableTable from "../../../../../components/TableLite/TableLiteScrollable";
import EmployeesWithoutGoalsCols from "./EmployeesWithoutGoalsCols";

import { router } from "../../../../../app/router";

import { useRbacPrimaryRolePermissions } from "../../../../../api/hooks";
import { useCompanySettings } from "../../../../../api/hooks/defs/company";
import {
	useInternalApiSearchService,
	useInternalApiSearchServicePrefetch,
} from "../../../../../api/hooks/defs/search-service";
import { useFeatureFlagQuery } from "../../../../../hooks/useFeatureFlag";

import { getInteralApiSearchServiceCsv } from "../../../../../api/services/defs/internal-api/search-service";

import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import EmptySearchTableMessage from "../../../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import { GOALS_SIDE_NAVIGATION_LINKS } from "../../constants";

const EMPLOYEES_WITHOUT_GOALS_TABLE_LIMIT = 25;

const EmployeesWithoutGoals = ({ navItems }: { navItems: Array<SideNavigationLink> }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const { width: containerWidth = 1 } = useResizeObserver({ ref: containerRef });
	const { data: companySettings, isLoading: isCompanySettingsLoading } = useCompanySettings();
	const { data: permissions } = useRbacPrimaryRolePermissions("goal");
	const [downloadCsvJobID, setDownloadCsvJobID] = useState<string | null>(null);
	const groupsFeatureEnabled = useFeatureFlagQuery("ff-is-groups-enabled", false);
	const { value: groupsEnabled } =
		companySettings?.functions?.groups?.state === "enabled" ? groupsFeatureEnabled : { value: false };

	const canUserCreateGoals = permissions?.includes("goals:createGoal");

	const { pageNumber, setPageNumber, sort, setSort, searchValue, debouncedSearchValue, setDebouncedSearch } =
		useInfoPageTemplateQueryState({
			defaultSortName: "name",
			defaultSortDirection: "asc",
			defaultPageNumber: 1,
		});

	const goalWizardBaseUrl = "/goal/wizard/";

	const queryArgs = useMemo(
		() => ({
			service: "employee" as const,
			filters: {
				name_re: debouncedSearchValue,
				without_goals: true,
			},
			sort: {
				name: sort.name,
				direction: sort.direction,
			},
			skip: (pageNumber - 1) * EMPLOYEES_WITHOUT_GOALS_TABLE_LIMIT,
			limit: EMPLOYEES_WITHOUT_GOALS_TABLE_LIMIT,
		}),
		[debouncedSearchValue, pageNumber, sort]
	);

	const { data, isLoading, refetch } = useInternalApiSearchService(queryArgs);

	useEffect(() => {
		// fetches any new data whenever the page is loaded
		refetch();
	}, []);

	// prefetch 2 pages ahead or before the current page if appropriate
	useInternalApiSearchServicePrefetch(queryArgs, 2);

	const parsedEmployeesResults = useMemo(() => {
		return (data?.results || []).map((employee) => {
			const parsedEmployee = EmployeeWithoutGoalsModel.parse(employee);
			return camelToSnake(parsedEmployee);
		});
	}, [data]);

	const columns = EmployeesWithoutGoalsCols({ groupsEnabled });

	const singleColumnWidth = useMemo(() => {
		return containerWidth / columns.length - 16;
	}, [containerWidth, columns.length]);

	const mainContent = (
		<div ref={containerRef}>
			<ScrollableTable
				rows={parsedEmployeesResults}
				headCellClass="h-9 flex items-center"
				isLoading={isCompanySettingsLoading || isLoading}
				activeSort={sort.name}
				activeSortDirection={sort.direction}
				onColumnClick={(column: string) => {
					setPageNumber(1);
					setSort({ name: column, direction: sort.direction === "asc" ? "desc" : "asc" });
				}}
				columns={columns.map((col) => ({
					...col,
					width: singleColumnWidth,
				}))}
				emptyChildren={<EmptySearchTableMessage mainText="No Employees Found" />}
			/>
			<Spacer height={3} />
			<Pagination
				pageCount={Math.ceil(
					(data?.total || EMPLOYEES_WITHOUT_GOALS_TABLE_LIMIT) / EMPLOYEES_WITHOUT_GOALS_TABLE_LIMIT
				)}
				currentPage={pageNumber}
				onClick={(page: number) => setPageNumber(page)}
			/>
			<Spacer height={2} />
		</div>
	);

	// page template elements:
	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={setDebouncedSearch}
			searchPlaceholder="Search Employees..."
		/>
	);

	const handleDownloadCsv = async () => {
		const response = await getInteralApiSearchServiceCsv({
			service: "employee" as const,
			filters: {
				name_re: debouncedSearchValue,
				without_goals: true,
			},
			sort: {
				name: sort.name,
				direction: sort.direction,
			},
			columns: ["name", ...(groupsEnabled ? ["group"] : []), "manager.name"],
			fileName: `Employees Without Goals${debouncedSearchValue ? ` - Search=${debouncedSearchValue}` : ""}`,
		});

		setDownloadCsvJobID(response.jobID);
	};

	const handleClearCsvDownload = () => {
		setDownloadCsvJobID(null);
	};

	const actionButtons = [
		<DownloadButton
			key="download-csv"
			autoDownload={true}
			jobID={downloadCsvJobID}
			isHollow={true}
			data-testid="downloadCSVButton"
			onClick={handleDownloadCsv}
			onDownload={handleClearCsvDownload}
		>
			Download CSV
		</DownloadButton>,
		canUserCreateGoals && (
			<ButtonStandard
				key="create-goal"
				onClick={() => {
					router.navigate(`${goalWizardBaseUrl}`);
				}}
				color="green"
				iconLeft={faPlus}
			>
				Create Goal
			</ButtonStandard>
		),
	];

	return (
		<InfoPageTemplate
			mainContent={mainContent}
			pageTitle="Company Goals"
			navigationLinks={navItems}
			actionButtons={actionButtons}
			toolbarActionRow={toolbarActionRow}
			activeNavigationLinkKey={GOALS_SIDE_NAVIGATION_LINKS[3].key}
		/>
	);
};

export default EmployeesWithoutGoals;
